import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, ExternalLink, Download, AlertTriangle, Sparkles } from 'lucide-react';

import { dataApi } from '../../services/api';
import { useLicence } from '../../context/LicenceContext';

import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Skeleton } from '../ui/skeleton';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';

/**
 * Licence & Billing — the General tab of organisation Settings.
 *
 * Lives under Settings rather than the user menu because a plan belongs to the
 * shop, not to whoever happens to be signed in.
 *
 * Answers the four questions someone actually opens it to ask:
 *   What are we on? What do we get? What have we used? What have we paid?
 *
 * Deliberately not a dashboard. No charts, no upsell banner over the top of
 * the plan they already bought, no per-feature comparison grid unless they ask
 * for one. Plans only appear when there's a decision to make — a shop on Pro
 * sees their plan, their usage and their invoices, and nothing else.
 *
 * Lemon Squeezy is Merchant of Record, so anything that touches money — card
 * details, cancelling, tax receipts — hands off to their customer portal
 * rather than being reimplemented here. That's why there is no "update card"
 * form: we never see the card.
 */

/** Human wording for a subscription state. Only shown when it isn't 'active'. */
const STATUS_LABELS = {
  trialing: { label: 'Trial', variant: 'secondary' },
  past_due: { label: 'Payment failed', variant: 'destructive' },
  unpaid: { label: 'Unpaid', variant: 'destructive' },
  paused: { label: 'Paused', variant: 'secondary' },
  cancelled: { label: 'Cancelling', variant: 'secondary' },
  expired: { label: 'Expired', variant: 'destructive' },
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return null;
  }
};

/**
 * Usage meter. Reads as reassurance while there's room and as a prompt only
 * once there isn't — the bar turns amber near the cap rather than living in a
 * permanent warning colour.
 */
const UsageMeter = ({ usage }) => {
  if (!usage) return null;

  if (usage.unlimited) {
    return (
      <div className="space-y-1">
        <div className="flex items-baseline justify-between">
          <span className="text-sm text-muted-foreground">AI damage assessments</span>
          <span className="text-sm font-medium">{usage.used} this month</span>
        </div>
        <p className="text-xs text-muted-foreground">Unlimited on your plan.</p>
      </div>
    );
  }

  const pct = usage.limit > 0 ? (usage.used / usage.limit) * 100 : 100;
  const nearLimit = pct >= 80;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-4">
        <span className="text-sm text-muted-foreground">AI damage assessments</span>
        <span className="text-sm font-medium">
          {usage.used} of {usage.limit} used
        </span>
      </div>
      <Progress
        value={pct}
        className={nearLimit ? '[&>div]:bg-amber-500' : undefined}
      />
      <p className="text-xs text-muted-foreground">
        {usage.exhausted
          ? 'Your allowance is used up for this month. Manual quoting is unaffected.'
          : `${usage.remaining} left`}
        {usage.resetsAt ? ` · Resets ${formatDate(usage.resetsAt)}` : ''}
      </p>
    </div>
  );
};

const PlanCard = ({ plan, isCurrent, onChoose, busy }) => (
  <Card className={isCurrent ? 'border-primary' : undefined}>
    <CardHeader className="space-y-1">
      <div className="flex items-center justify-between gap-2">
        <CardTitle className="text-lg">{plan.name}</CardTitle>
        {isCurrent && <Badge variant="secondary">Current</Badge>}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-semibold">{plan.priceLabel}</span>
        {plan.interval && (
          <span className="text-sm text-muted-foreground">/{plan.interval}</span>
        )}
      </div>
      <p className="text-sm text-muted-foreground">{plan.tagline}</p>
    </CardHeader>
    <CardContent className="space-y-4">
      <ul className="space-y-2">
        {(plan.features || []).map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm">
            <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {!isCurrent && plan.purchasable && (
        <Button className="w-full" onClick={() => onChoose(plan.id)} disabled={busy}>
          {busy ? 'Opening checkout…' : `Choose ${plan.name}`}
        </Button>
      )}
    </CardContent>
  </Card>
);

const LicenceBilling = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    refresh: refreshLicence,
    isAdmin,
    licence,
    loading: licenceLoading,
  } = useLicence();

  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [busyPlan, setBusyPlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await dataApi.licenceBilling();
      setBilling(data);
      setForbidden(false);
      return data;
    } catch (err) {
      // The function returns 403 for non-admins; everything else is a genuine
      // error worth telling them about.
      if (/administrator|access/i.test(err.message || '')) {
        setForbidden(true);
      } else {
        console.error('Failed to load billing:', err);
        toast.error('Could not load your billing details');
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Only admins have anything to fetch. Everyone else sees the plan and
    // usage they can already read from the licence row — no point calling a
    // function that will only refuse them.
    if (licenceLoading) return;
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    load();
  }, [isAdmin, licenceLoading, load]);

  /**
   * Coming back from a successful checkout. The subscription is real the moment
   * Lemon Squeezy takes the payment, but our licence row only updates when the
   * webhook lands — usually a second or two behind. Re-poll a few times so the
   * page shows the new plan instead of the old one.
   */
  useEffect(() => {
    if (searchParams.get('checkout') !== 'success') return;

    toast.success('Thanks — your plan is being activated');
    setSearchParams({}, { replace: true });

    let attempts = 0;
    const timer = setInterval(async () => {
      attempts += 1;
      const data = await load();
      await refreshLicence();

      if (data?.subscribed || attempts >= 5) {
        clearInterval(timer);
        if (data?.subscribed) toast.success(`You're on ${data.planName}`);
      }
    }, 3000);

    return () => clearInterval(timer);
  }, [searchParams, setSearchParams, load, refreshLicence]);

  const handleUpgrade = async (planId) => {
    try {
      setBusyPlan(planId);
      const url = await dataApi.licenceCheckout(planId);
      if (!url) throw new Error('Checkout was not available');
      // Full navigation rather than a popup — popup blockers make an upgrade
      // silently do nothing, which is the worst possible failure here.
      window.location.href = url;
    } catch (err) {
      console.error('Checkout failed:', err);
      toast.error(err.message || 'Could not start checkout. Please try again.');
      setBusyPlan(null);
    }
  };

  const handlePortal = async () => {
    try {
      setOpeningPortal(true);
      const url = await dataApi.licencePortal();
      if (!url) throw new Error('The billing portal was not available');
      window.location.href = url;
    } catch (err) {
      console.error('Portal failed:', err);
      toast.error(err.message || 'Could not open the billing portal. Please try again.');
      setOpeningPortal(false);
    }
  };

  // =====================================================
  // States
  // =====================================================

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-56 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  // Not an administrator: show what the plan is and how much of it is left,
  // which is genuinely useful to anyone running assessments, but nothing about
  // money. Read straight from the licence row — no invoices, no portal, no
  // upgrade buttons that would only fail.
  if (!isAdmin || forbidden) {
    const usage = licence?.usage;
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{licence?.planName || 'Current plan'}</CardTitle>
          {licence?.plan?.tagline && (
            <p className="text-sm text-muted-foreground">{licence.plan.tagline}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <UsageMeter usage={usage} />
          <p className="text-sm text-muted-foreground">
            Billing is managed by your organisation&rsquo;s administrators.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!billing) {
    return (
      <Card>
        <CardContent className="py-12 text-center space-y-4">
          <p className="text-muted-foreground">We couldn't load your billing details.</p>
          <Button variant="outline" onClick={() => { setLoading(true); load(); }}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }

  const statusMeta = STATUS_LABELS[billing.status];
  const catalogue = billing.catalogue || [];
  const upgradeable = catalogue.filter((p) => p.id !== billing.planId && p.purchasable);
  // A shop on the free plan is exactly who the plans are for, so show them
  // without making anyone hunt for a button. Paid shops keep them tucked away.
  const plansVisible = showPlans || (!billing.subscribed && upgradeable.length > 0);

  return (
    <div className="space-y-6">
      {billing.status === 'past_due' && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>We couldn't take your last payment</AlertTitle>
          <AlertDescription>
            Your plan is still active for now. Update your payment method to avoid
            losing access to it.
          </AlertDescription>
        </Alert>
      )}

      {billing.cancelsAt && billing.status === 'cancelled' && (
        <Alert>
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Your plan ends on {formatDate(billing.cancelsAt)}</AlertTitle>
          <AlertDescription>
            You keep everything in your plan until then, and move to Free afterwards.
          </AlertDescription>
        </Alert>
      )}

      {/* ---- Current plan ---------------------------------------------- */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <CardTitle className="text-xl">{billing.planName}</CardTitle>
              {statusMeta && <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>}
            </div>

            <div className="flex flex-wrap gap-2">
              {billing.subscribed && (
                <Button variant="outline" onClick={handlePortal} disabled={openingPortal}>
                  {openingPortal ? 'Opening…' : 'Manage billing'}
                  {!openingPortal && <ExternalLink className="ml-2 h-4 w-4" />}
                </Button>
              )}
              {upgradeable.length > 0 && !plansVisible && (
                <Button onClick={() => setShowPlans(true)}>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Change plan
                </Button>
              )}
            </div>
          </div>
          {billing.plan?.tagline && (
            <p className="text-sm text-muted-foreground">{billing.plan.tagline}</p>
          )}
        </CardHeader>

        <CardContent className="space-y-6">
          <UsageMeter usage={billing.usage} />

          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-2">
              <h3 className="text-sm font-medium">What's included</h3>
              <ul className="space-y-1.5">
                {(billing.plan?.features || []).map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" aria-hidden="true" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {billing.subscribed && (
              <dl className="space-y-2 text-sm">
                {billing.renewsAt && billing.status !== 'cancelled' && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Renews</dt>
                    <dd>{formatDate(billing.renewsAt)}</dd>
                  </div>
                )}
                {billing.card?.lastFour && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Payment method</dt>
                    <dd className="capitalize">
                      {billing.card.brand} ···· {billing.card.lastFour}
                    </dd>
                  </div>
                )}
                {billing.billingEmail && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">Receipts go to</dt>
                    <dd className="truncate">{billing.billingEmail}</dd>
                  </div>
                )}
              </dl>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ---- Plans ------------------------------------------------------- */}
      {plansVisible && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Plans</h2>
            {showPlans && (
              <Button variant="ghost" size="sm" onClick={() => setShowPlans(false)}>
                Hide
              </Button>
            )}
          </div>

          {!billing.billingConfigured ? (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Paid plans aren't available just yet. Get in touch if you need more
                than your current allowance and we'll sort you out.
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-3">
              {catalogue.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  isCurrent={plan.id === billing.planId}
                  onChoose={handleUpgrade}
                  busy={busyPlan === plan.id}
                />
              ))}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Prices in NZD. Payments are handled by Lemon Squeezy, our authorised
            reseller — any tax or GST due is shown at checkout and on your invoice.
          </p>
        </div>
      )}

      {/* ---- Invoices ---------------------------------------------------- */}
      {billing.subscribed && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {billing.invoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No invoices yet. Your first one will appear here after your next payment.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="py-2 pr-4 font-medium">Date</th>
                      <th className="py-2 pr-4 font-medium">Amount</th>
                      <th className="py-2 pr-4 font-medium">Status</th>
                      <th className="py-2 font-medium sr-only">Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {billing.invoices.map((invoice) => (
                      <tr key={invoice.id} className="border-b last:border-0">
                        <td className="py-2 pr-4 whitespace-nowrap">
                          {formatDate(invoice.createdAt)}
                        </td>
                        <td className="py-2 pr-4 whitespace-nowrap">{invoice.total}</td>
                        <td className="py-2 pr-4">
                          <span className="text-muted-foreground">
                            {invoice.refunded ? 'Refunded' : invoice.statusLabel || invoice.status}
                          </span>
                        </td>
                        <td className="py-2 text-right">
                          {invoice.invoiceUrl && (
                            <Button asChild variant="ghost" size="sm">
                              <a href={invoice.invoiceUrl} target="_blank" rel="noreferrer">
                                <Download className="h-4 w-4 mr-1" />
                                Invoice
                              </a>
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LicenceBilling;
